/**
 * The plate as the frame draws it (D266): a picture, or — where the pack's
 * moving plate would not fit the budget as an animated WebP — a video, in
 * the same box, so the sprites composite over it exactly as over a still.
 *
 * @remarks
 * A video plate is drawn ONLY where it moves. The still it owes (the
 * schema refuses a video plate without one) stands in everywhere else: it
 * is the poster the video shows before its first frame and for good when a
 * play is refused (autoplay blocked, data saver), so nothing breaks; the
 * reduced-motion viewer's picture, which `motionSourceOf` has already
 * chosen before this module sees the plate; the departing copy of a
 * dissolve, which fades for a quarter second and need not start a second
 * decoder to do it; and the lightbox, where a full-screen loop adds nothing
 * the prose needs.
 *
 * A `<video>` has no `alt`, so the plate's caption rides a `role="img"`
 * wrapper and the element itself is hidden from the accessibility tree: a
 * screen-reader user hears the frame once, as from an `<img>` (D89 rule 6).
 *
 * D99: a scene commits only when every layer is ready — and for a video
 * plate the layer the dissolve shows is its poster. The still is the loop's
 * own frame, the same pixels outside the moving region, so when the video
 * starts it changes nothing but what moves; the scene does not wait for it.
 * Waiting would need the video loaded in a second, detached element, which
 * the shown one is not promised to share bytes with (a media cache is the
 * browser's business), and would hold the room's picture behind a network
 * the still has already crossed.
 *
 * @packageDocumentation
 */

/** A plate as the scene holds it: the file chosen for this viewer, and the still it owes or is. */
export interface ScenePlate {
	/** The authored frame-caption (D89 rule 6), verbatim. */
	readonly alt: string;
	/** The file chosen for this viewer — the still, when they asked for reduced motion (D263). */
	readonly src: string;
	/** The plate's still: its `reducedMotionSrc`, or `src` itself for a plate that does not move. */
	readonly stillSrc: string;
}

/**
 * Whether a served asset is a video — the plate's file type is the
 * discriminant (D266). The URL's path is read, never its query: a resolved
 * asset may carry a cache-busting one.
 *
 * @param url - A resolved asset URL, absolute or root-relative
 * @returns Whether it names an MP4
 */
export const isVideoSource = (url: string): boolean => /^[^?#]*\.mp4(?:[?#]|$)/iu.test(url);

/**
 * The file a plate draws wherever it does not play — a departing copy and
 * the lightbox: the still for a video, the plate's own file otherwise (an
 * animated WebP plays wherever an `<img>` does, as it always has).
 *
 * @param plate - The scene's plate
 * @returns The file to draw as a picture
 */
export const restingPlateSourceOf = (plate: ScenePlate): string => (isVideoSource(plate.src) ? plate.stillSrc : plate.src);

/** Decode one image source, tolerating environments and assets that cannot (jsdom; a 404 — the img still paints). */
export const decodeImage = async (src: string): Promise<void> => {
	const image = new Image();

	image.src = src;

	if (typeof image.decode === 'function') {
		await image.decode().catch(() => undefined);
	}
};

/**
 * What a plate needs before its scene may dissolve in (D99): the picture the
 * dissolve will show decoded — for a video, its poster.
 *
 * @param plate - The incoming scene's plate
 */
export const decodePlate = async (plate: ScenePlate): Promise<void> => decodeImage(restingPlateSourceOf(plate));

/**
 * Keep a plate's video muted at the attribute as well as the property: React
 * sets `muted` only as a property, and an inline autoplay is allowed only to
 * a video that is muted by the time the browser asks.
 *
 * @param video - The mounted element
 */
const keepMuted = (video: HTMLVideoElement | null) => {
	if (video !== null) {
		video.defaultMuted = true;
		video.muted = true;
	}
};

/** Props for {@link ScenePlateComponent}. */
interface ScenePlateComponentProps {
	/** The plate's layer classes (arriving, in the frame). */
	readonly className: string;
	/** The plate to draw. */
	readonly plate: ScenePlate;
}

/**
 * Draw the arriving plate: an `<img>`, or a silent, looping, inline video
 * over its still with the caption on its wrapper.
 *
 * @param props - {@link ScenePlateComponentProps}
 * @returns The plate layer
 */
export const ScenePlateComponent = ({ className, plate }: ScenePlateComponentProps) =>
	isVideoSource(plate.src) ? (
		<div aria-label={plate.alt} className={className} role="img">
			<video
				aria-hidden="true"
				autoPlay
				className="room-image__video"
				disablePictureInPicture
				loop
				muted
				playsInline
				poster={plate.stillSrc}
				preload="auto"
				ref={keepMuted}
				src={plate.src}
			/>
		</div>
	) : (
		<img alt={plate.alt} className={className} src={plate.src} />
	);
